import { Button, Checkbox, cn, Stack } from '@landler/component-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Action } from '@/api/rest/resources/types/conversation';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { isNullish } from '@/utils/booleans';

export const ACTION_BUTTON_STYLE =
  'w-full p-2 first-letter:uppercase font-normal typography-body2 text-text-primary border-neutral-black-12 bg-neutral-black-4 rounded-md hover:bg-neutral-black-8 hover:text-primary-100 hover:border-graph-tooltip';
export const DISABLED_ACTION_BUTTON_STYLE =
  'w-full p-2 first-letter:uppercase font-normal typography-body2 text-text-secondary border-neutral-black-12 bg-neutral-black-4 cursor-auto rounded-md hover:bg-neutral-black-4 hover:text-text-secondary hover:border-neutral-black-12';

const TextInputAction = ({
  action,
  onExecute,
  conversationId,
  isStreamInProgress,
  disabled,
}: {
  action: Action;
  onExecute: (actions: Action[], conversationId?: string) => void;
  conversationId: string | null;
  isStreamInProgress: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  const handleSubmit = () => {
    const textAction: Action = {
      ...action,
      name: text,
      context: {
        ...action.context,
        selected_values: text,
      },
    };
    onExecute([textAction], conversationId || undefined);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  return (
    <Stack
      direction='row'
      className={cn(
        disabled ? DISABLED_ACTION_BUTTON_STYLE : ACTION_BUTTON_STYLE,
        'bg-white-100 border gap-2 items-end p-4 rounded-3xl w-full shadow-md hover:bg-white-100 hover:text-text-primary hover:border-graph-tooltip',
      )}
    >
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        placeholder={t('shared.onboarding.goalsContext.placeholder')}
        className={cn(
          disabled ? DISABLED_ACTION_BUTTON_STYLE : ACTION_BUTTON_STYLE,
          'hover:bg-white-100 hover:text-text-primary hover:border-graph-tooltip',
          'normal-case flex-1 border-none outline-none resize-none bg-white-100 content-center',
        )}
        style={{ maxHeight: '200px', minHeight: '40px', height: 'auto' }}
        disabled={isStreamInProgress || disabled}
      />
      <Button
        onClick={handleSubmit}
        disabled={isStreamInProgress || !text.trim() || disabled}
        variant='contained'
        className='rounded h-10 w-10 flex justify-center items-center'
      >
        <Icon icon='ri-arrow-up-line' />
      </Button>
    </Stack>
  );
};

export const PromptedActions = ({
  actions,
  onExecute,
  conversationId,
  isStreamInProgress,
  disabled = true,
}: {
  actions: Action[];
  onExecute: (actions: Action[], conversationId?: string) => void;
  conversationId: string | null;
  isStreamInProgress: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [selectedActions, setSelectedActions] = useState<Action[]>([]);
  const allowMultipleActions =
    actions.some((action) => action.context?.multi_select) ||
    actions.some((action) => action.context?.actions?.some((nested) => nested.context?.multi_select));

  const handleSubmit = () => {
    if (!disabled) {
      onExecute(selectedActions, conversationId || undefined);
      setSelectedActions([]);
    }
  };

  const handleToggle = (action: Action, event?: React.MouseEvent) => {
    event?.stopPropagation();

    if (allowMultipleActions) {
      setSelectedActions((prev) =>
        prev.map((a) => a.context?.selected_values || a.id).includes(action.context?.selected_values || action.id)
          ? prev.filter((v) => (v.context?.selected_values || v.id) !== (action.context?.selected_values || action.id))
          : [...prev, action],
      );
    } else {
      setSelectedActions([action]);
    }
  };

  const MultiSelectAction = ({ action, disabledArg }: { action: Action; disabledArg?: boolean }) => {
    const actionId = action.context?.selected_values || action.id;
    const isSelected = selectedActions.map((a) => a.context?.selected_values || a.id).includes(actionId);
    return (
      <Stack className='gap-2'>
        <Stack direction='row' className={cn('gap-3 items-start cursor-pointer p-3 rounded hover:bg-neutral-black-4')}>
          <Checkbox disabled={disabledArg} checked={isSelected} onCheckedChange={() => handleToggle(action)} />
          <Stack className='gap-1' onClick={(e) => !disabledArg && handleToggle(action, e)}>
            <span className={cn(ACTION_BUTTON_STYLE, 'text-text-primary bg-transparent')}>{action.name}</span>
            {action.description && (
              <span className={cn(ACTION_BUTTON_STYLE, 'typography-caption bg-transparent')}>{action.description}</span>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack className='gap-4 w-full pt-3'>
      <Stack className='gap-2 w-full'>
        {actions.map((action, index) => {
          const isTextInputAction = action.context?.action_type === 'text_input';
          if (isTextInputAction) {
            return (
              <TextInputAction
                key={`text-input-action-${action.id}-${index}`}
                action={action}
                onExecute={onExecute}
                conversationId={conversationId}
                isStreamInProgress={isStreamInProgress}
                disabled={disabled}
              />
            );
          }

          const nestedActions = action.context?.actions;

          if (isNullish(nestedActions) || nestedActions.length === 0) {
            if (allowMultipleActions) {
              return <MultiSelectAction key={`${action.id}-${index}`} action={action} disabledArg={disabled} />;
            }
            return (
              <Button
                key={`${action.id}-${index}`}
                onClick={() => !disabled && onExecute([action], conversationId || undefined)}
                disabled={isStreamInProgress}
                variant='outline'
                className={cn(disabled ? DISABLED_ACTION_BUTTON_STYLE : ACTION_BUTTON_STYLE, 'pl-2 justify-start')}
                leftAdornment={<Icon icon='ri-sparkling-2-line' className='text-primary-100 text-lg' />}
              >
                {action.name}
              </Button>
            );
          }
          return (
            <Stack key={`action-with-nested-${action.id}-${index}`}>
              <span
                key={`action-name-${action.id}-${index}`}
                className={cn(
                  disabled ? DISABLED_ACTION_BUTTON_STYLE : ACTION_BUTTON_STYLE,
                  'text-text-primary justify-start',
                )}
              >
                {action.name}
              </span>
              {nestedActions?.map((nestedAction, idx) => (
                <MultiSelectAction
                  key={`nested-action-${nestedAction.id}-${idx}`}
                  action={nestedAction}
                  disabledArg={disabled}
                />
              ))}
            </Stack>
          );
        })}
      </Stack>

      {allowMultipleActions && (
        <Button
          onClick={handleSubmit}
          disabled={disabled || isStreamInProgress}
          variant='outline'
          className='w-fit self-end'
        >
          {t('global.ui.buttons.continue')}
        </Button>
      )}
    </Stack>
  );
};
